import type { HandyoutSectionKey, ModuleRevisionBatch } from "@/types/handyout-modules";
import type { DraggableModuleType, PageModule } from "@/types/modules";
import { DEMO_MODULES } from "@/utils/demo-data";

export const PRE_DECK_TITLE = "Strategy 2030";

export const PRE_DECK_FILE = { name: "Strategy-2030.pptx", size: "4.2 MB" };

export const PRE_SLIDE_COLORS = ["#1F4E79", "#E8672E", "#2E8B57", "#F2C14E", "#6B7280"];

export const PRE_BASE_COLOR = PRE_SLIDE_COLORS[0]!;

type ModuleSpec =
  | DraggableModuleType
  | { type: DraggableModuleType; overrides: Record<string, unknown> };

type SlideSpec = {
  key: string;
  title: string;
  modules: ModuleSpec[];
  lowQuality?: boolean;
};

const SLIDE_SPECS: SlideSpec[] = [
  { key: "standing", title: "Where we stand", modules: ["ring-kpis"] },
  { key: "strategy", title: "Strategy 2030", modules: ["insight-panel"] },
  {
    key: "revenue",
    title: "Revenue and costs",
    modules: [{ type: "bar", overrides: { title: "Revenue and costs" } }],
  },
  { key: "mix", title: "Revenue mix", modules: ["breakdown"] },
  { key: "plan", title: "Project plan", modules: ["gantt-phases"], lowQuality: true },
  { key: "schedule", title: "Release schedule", modules: ["timeline-stepper"] },
  { key: "customers", title: "Key customers", modules: ["table"] },
];

export type PreSlide = {
  slideId: string;
  number: number;
  title: string;
  types: DraggableModuleType[];
  lowQuality: boolean;
};

function specType(spec: ModuleSpec): DraggableModuleType {
  return typeof spec === "string" ? spec : spec.type;
}

export const PRE_SLIDES: PreSlide[] = SLIDE_SPECS.map((spec, index) => ({
  slideId: `pre-slide-${spec.key}`,
  number: index + 1,
  title: spec.title,
  types: spec.modules.map(specType),
  lowQuality: spec.lowQuality === true,
}));

export const PRE_SLIDE_PAGES: PageModule[] = SLIDE_SPECS.map((spec) => ({
  slideId: `pre-slide-${spec.key}`,
  title: spec.title,
  modules: spec.modules.map((moduleSpec) => {
    const type = specType(moduleSpec);
    const overrides = typeof moduleSpec === "string" ? {} : moduleSpec.overrides;
    return { ...DEMO_MODULES[type], ...overrides, id: `pre-${spec.key}-${type}` };
  }),
}));

export function preSlidePages(keys: string[]): PageModule[] {
  return keys
    .map((key) => PRE_SLIDE_PAGES.find((page) => page.slideId === `pre-slide-${key}`))
    .filter((page): page is PageModule => page !== undefined);
}

export const PRE_HERO_PAGES = preSlidePages(["strategy", "revenue", "schedule"]);

export const PRE_CHAT_SLIDE_ID = "pre-slide-revenue";

export const PRE_GENERAL_PAGES: Record<HandyoutSectionKey, PageModule> = {
  intro: {
    slideId: "intro",
    title: PRE_DECK_TITLE,
    modules: [
      {
        id: "pre-intro",
        type: "intro",
        title: PRE_DECK_TITLE,
        summary:
          "Where the business stands today, how revenue is split, and the plan to make half of all revenue recurring by 2030.",
        author: { firstName: "Your", lastName: "name", email: "", role: "Role or title" },
      },
    ],
  },
  toc: {
    slideId: "toc",
    title: "Table of contents",
    modules: [
      {
        id: "pre-toc",
        type: "toc",
        title: "Table of contents",
        data: {
          "1": { title: "Where we stand", slide_range: [1, 2] },
          "2": {
            title: "Revenue",
            slide_range: [3, 4],
            children: {
              "2.1": { title: "Revenue and costs", slide_range: [3, 3] },
              "2.2": { title: "Revenue mix", slide_range: [4, 4] },
            },
          },
          "3": { title: "Delivery", slide_range: [5, 6] },
          "4": { title: "Key customers", slide_range: [7, 7] },
        },
      },
    ],
  },
  "key-takeaways": {
    slideId: "key-takeaways",
    title: "Key takeaways",
    modules: [
      {
        id: "pre-takeaways",
        type: "key-takeaways",
        title: "Key takeaways",
        data: {
          "1": {
            heading: "Half of all revenue is to be recurring by 2030",
            body: "The share was 38 % in 2026. Standard products with a monthly price replace one-off projects.",
            slide: 2,
          },
          "2": {
            heading: "Managed network leads the revenue mix",
            body: "It is the largest product line, ahead of security services and cloud operations.",
            slide: 4,
          },
          "3": {
            heading: "The build phase is under way",
            body: "Planning and design are done. QA and launch follow.",
            slide: 5,
          },
        },
      },
    ],
  },
  faq: {
    slideId: "faq",
    title: "FAQ",
    modules: [
      {
        id: "pre-faq",
        type: "faq",
        title: "FAQ",
        data: {
          "1": {
            category: "Strategy",
            question: "What counts as recurring revenue?",
            answer: "Revenue from contracts with a monthly price, such as managed services and support.",
          },
          "2": {
            category: "Strategy",
            question: "Which product line is the largest?",
            answer: "Managed network, followed by security services and cloud operations.",
          },
          "3": {
            category: "Timeline",
            question: "When is the launch?",
            answer: "The release schedule puts the launch in early October.",
          },
        },
      },
    ],
  },
  feedback: {
    slideId: "feedback",
    title: "Feedback",
    modules: [
      {
        id: "pre-feedback",
        type: "feedback",
        title: "How was it?",
        data: { session_id: "" },
      },
    ],
  },
};

export const PRE_GENERATION_PROGRESS = { current: 5, total: SLIDE_SPECS.length, pct: 84 };

function slideChange(
  key: string,
  type: DraggableModuleType,
  change: ModuleRevisionBatch["changes"][number]["change_type"],
  title: string | null = null,
): ModuleRevisionBatch["changes"][number] {
  return {
    module_id: `pre-${key}-${type}`,
    change_type: change,
    type,
    title,
    slide_id: `pre-slide-${key}`,
    section: "slide",
  };
}

function batch(
  id: string,
  source: ModuleRevisionBatch["source"],
  changes: ModuleRevisionBatch["changes"],
): ModuleRevisionBatch {
  return {
    batch_id: id,
    created_at: "2026-10-12T09:00:00.000Z",
    updated_at: "2026-10-12T09:00:00.000Z",
    source,
    actor_user_id: null,
    module_message_id: null,
    reverts_batch_id: null,
    changes,
  };
}

export const PRE_HISTORY_BATCHES: ModuleRevisionBatch[] = [
  batch("pre-history-1", "ai_accept", [slideChange("revenue", "bar", "upsert", "Revenue and costs")]),
  batch("pre-history-2", "editor", [slideChange("customers", "table", "reorder", "Customers")]),
  batch("pre-history-3", "ai_fill", [slideChange("schedule", "timeline-stepper", "upsert", "Release schedule")]),
  batch("pre-history-4", "editor", [slideChange("schedule", "timeline-stepper", "insert")]),
  batch("pre-history-5", "pipeline", [
    slideChange("standing", "ring-kpis", "insert", "Service KPIs"),
    slideChange("strategy", "insight-panel", "insert", "Strategy 2030"),
    slideChange("revenue", "bar", "insert", "Revenue and costs"),
  ]),
];

export const PRE_HISTORY_TIMES = ["10:42", "10:37", "10:31", "10:29", "10:12"];
